/**
 * Contrast utilities
 *
 * Zero-dependency WCAG relative-luminance helpers used to pick a readable
 * foreground (black/white) for an arbitrary background colour. Used by the
 * configurable Colors feature so user-chosen colours always render legible
 * text without requiring a separate contrast library.
 *
 * @module shared/lib/contrast
 */

/** A foreground colour guaranteed to be readable on a given background. */
export type ReadableForeground = '#000000' | '#ffffff';

/**
 * Parse a hex colour string (`#rgb`, `#rrggbb`, with or without the leading
 * `#`) into 0-255 RGB components. Returns `null` for invalid input.
 */
export function hexToRgb(
  hex: string
): { readonly r: number; readonly g: number; readonly b: number } | null {
  const normalized = hex.trim().replace(/^#/, '');

  if (normalized.length === 3) {
    const r = normalized[0];
    const g = normalized[1];
    const b = normalized[2];
    if (!r || !g || !b) return null;
    const value = `${r}${r}${g}${g}${b}${b}`;
    return hexToRgb(value);
  }

  if (normalized.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }

  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

/**
 * Linearize a single 0-255 sRGB channel to its 0-1 linear value (WCAG).
 */
function linearizeChannel(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/**
 * Compute the WCAG relative luminance (0-1) of a hex colour. Returns `0`
 * (treated as dark) for unparseable input.
 */
export function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const r = linearizeChannel(rgb.r);
  const g = linearizeChannel(rgb.g);
  const b = linearizeChannel(rgb.b);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Pick a readable foreground (`#000000` or `#ffffff`) for the given background
 * colour using its WCAG relative luminance. Light backgrounds get black text,
 * dark backgrounds get white text. Falls back to black for invalid input.
 *
 * @param backgroundHex - Background colour as a hex string.
 * @returns Black for light backgrounds, white for dark backgrounds.
 */
export function getReadableForeground(
  backgroundHex: string
): ReadableForeground {
  return getRelativeLuminance(backgroundHex) > 0.179 ? '#000000' : '#ffffff';
}
